import { Check, Copy, ExternalLink } from "lucide-react"
import { useMemo, useState } from "react"

import claudeIcon from "@/assets/logos/claude-icon.svg"
import openaiIcon from "@/assets/logos/openai-icon.svg"
import perplexityIcon from "@/assets/logos/perplexity-icon.svg"
import { OptionCard } from "@/components/agents/setup/receptionist/option-card"
import { formatPhoneDisplay } from "@/components/agents/setup/receptionist/phone-utils"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"

// Common US business phone providers seeded into the AI prompt. The list isn't
// exhaustive — "Other" captures an unlisted provider by name, and "I don't
// know" hands the identification job to the assistant. Line type (mobile /
// landline / VoIP) is intentionally NOT collected: it's implied for the VoIP
// providers and, for the carriers where it's ambiguous, the prompt tells the
// assistant to just ask.
const PROVIDERS = [
  "Verizon",
  "AT&T",
  "T-Mobile",
  "Comcast Business",
  "Spectrum Business",
  "Google Voice",
  "Grasshopper",
  "Ooma",
  "RingCentral",
  "Vonage",
] as const

// Sentinel tile values that aren't literal provider names.
const OTHER = "Other"
const UNKNOWN = "I don't know"

// One line of context so the assistant understands the goal — routing an
// existing business number to the receptionist — rather than guessing intent.
const CONTEXT =
  "I use an AI phone receptionist to answer my calls, and I want callers who dial my existing business number to reach it."

// Modern assistants ask their own clarifying questions, so we don't script the
// whole flow. We only nudge the one non-obvious choice (after-hours forwarding,
// which it won't surface on its own) and add a guardrail against confidently
// wrong steps for a provider it isn't sure about.
const ASK_DETAILS =
  "Before giving me steps, ask whether I want to forward all calls, only the ones I miss, or only after-hours — and anything else you need. If you're not certain of the exact steps for my provider, point me to their official support page rather than guessing."

function buildPrompt(opts: {
  provider: string
  otherProvider: string
  receptionistNumber: string
}): string {
  const { provider, otherProvider, receptionistNumber } = opts
  const target = receptionistNumber || "my receptionist's number"

  if (provider === UNKNOWN) {
    return [
      CONTEXT,
      `I want to forward my calls to ${target}, but I'm not sure who my phone provider is — help me figure that out first (tell me what to check, e.g. my phone bill or account), then give me exact, numbered step-by-step instructions to set up call forwarding, including any star codes (like *72) or web/admin-portal steps.`,
      `${ASK_DETAILS} Keep it concise.`,
    ].join(" ")
  }

  const name =
    (provider === OTHER ? otherProvider.trim() : provider) ||
    "my phone provider"
  return [
    CONTEXT,
    `My phone provider is ${name}, and I want to forward my calls to ${target}.`,
    `Please give me exact, numbered step-by-step instructions to set up call forwarding with ${name}, including any star codes (like *72) or web/admin-portal navigation.`,
    `${ASK_DETAILS} Keep it concise.`,
  ].join(" ")
}

// Each assistant prefills its composer via a `q` query param.
const HANDOFFS: { name: string; logo: string; url: (q: string) => string }[] = [
  {
    name: "Claude",
    logo: claudeIcon,
    url: (q) => `https://claude.ai/new?q=${q}`,
  },
  {
    name: "ChatGPT",
    logo: openaiIcon,
    url: (q) => `https://chatgpt.com/?q=${q}`,
  },
  {
    name: "Perplexity",
    logo: perplexityIcon,
    url: (q) => `https://www.perplexity.ai/search?q=${q}`,
  },
]

export function CallForwardingModal({
  open,
  onOpenChange,
  receptionistNumber,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  receptionistNumber: string | null
}) {
  const [provider, setProvider] = useState("")
  const [otherProvider, setOtherProvider] = useState("")
  const [numberCopied, setNumberCopied] = useState(false)

  const forwardTo = formatPhoneDisplay(receptionistNumber)
  // Handoff is ready once we can write a useful prompt: a real provider is
  // picked, "I don't know" (the assistant identifies it), or "Other" with a
  // name typed in.
  const canGenerate =
    provider === UNKNOWN ||
    (provider === OTHER ? otherProvider.trim().length > 0 : provider !== "")

  const prompt = useMemo(
    () =>
      buildPrompt({ provider, otherProvider, receptionistNumber: forwardTo }),
    [provider, otherProvider, forwardTo],
  )

  async function copyForwardTo() {
    try {
      await navigator.clipboard.writeText(forwardTo)
      setNumberCopied(true)
      setTimeout(() => setNumberCopied(false), 1500)
    } catch {
      /* clipboard unavailable — no-op */
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Use your existing number</DialogTitle>
          <DialogDescription>
            Keep your current business number and forward its calls to your
            receptionist. Tell us your phone provider and we&rsquo;ll generate
            exact setup instructions.
          </DialogDescription>
        </DialogHeader>

        {/* Forward-to target — the receptionist's provisioned number. */}
        <div className="flex items-center justify-between gap-4 rounded-lg border px-4 py-3">
          <div className="min-w-0">
            <p className="text-sm font-medium">Forward calls to</p>
            <p className="text-muted-foreground truncate text-sm">
              {forwardTo || "Your receptionist number"}
            </p>
          </div>
          {forwardTo && (
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => void copyForwardTo()}
            >
              {numberCopied ? (
                <Check className="size-4" />
              ) : (
                <Copy className="size-4" />
              )}
              {numberCopied ? "Copied" : "Copy"}
            </Button>
          )}
        </div>

        <div className="space-y-2">
          <Label>Phone provider</Label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {[...PROVIDERS, OTHER, UNKNOWN].map((p) => (
              <OptionCard
                key={p}
                active={provider === p}
                onClick={() => setProvider(p)}
                className="p-3 text-center text-sm font-medium"
              >
                {p}
              </OptionCard>
            ))}
          </div>
          {provider === OTHER && (
            <Input
              autoFocus
              placeholder="Who's your phone provider?"
              value={otherProvider}
              onChange={(e) => setOtherProvider(e.target.value)}
            />
          )}
        </div>

        {/* Handoff — the generated prompt is passed straight to the assistant. */}
        <div className="border-t pt-4">
          <div className="grid grid-cols-3 gap-3">
            {HANDOFFS.map((h) => (
              <button
                key={h.name}
                type="button"
                disabled={!canGenerate}
                onClick={() =>
                  window.open(
                    h.url(encodeURIComponent(prompt)),
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
                className={cn(
                  "border-border relative flex flex-col items-center justify-center gap-2 rounded-lg border px-3 py-4 text-center transition-colors",
                  canGenerate
                    ? "hover:bg-muted/40"
                    : "cursor-not-allowed opacity-50",
                )}
              >
                <ExternalLink className="text-muted-foreground absolute top-2 right-2 size-3.5" />
                <img src={h.logo} alt="" className="size-8" />
                <span className="text-sm font-medium">Open in {h.name}</span>
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
